import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { ArrowBack, Delete, Image as ImageIcon, Save, ShoppingCart } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import type { Pacote, PacoteInput } from '../../types'
import { apiService } from '../../services/api'

const brl = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const AdminPacoteEditPage: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams()
  const pacoteId = params.id || ''

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<Pacote | null>(null)

  const [vip, setVip] = useState(false)
  const [warzoneCoin, setWarzoneCoin] = useState(false)
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [preco, setPreco] = useState('0')
  const [pontos, setPontos] = useState('0')
  const [diasVip, setDiasVip] = useState('0')
  const [descricaoVip, setDescricaoVip] = useState('')
  const [imagem, setImagem] = useState<File | null>(null)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const canSave = useMemo(() => !!data && !loading, [data, loading])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const p = await apiService.buscarPacote(pacoteId)
        if (!mounted) return
        setData(p)
        setVip(!!p.vip)
        setWarzoneCoin(!!p.warzoneCoin)
        setNome(p.nome ?? '')
        setDescricao(p.descricao ?? '')
        setPreco(String(p.preco ?? 0))
        setPontos(String(p.pontos ?? 0))
        setDiasVip(String(p.diasVip ?? 0))
        setDescricaoVip(p.descricaoVip ?? '')
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao buscar pacote.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    if (pacoteId) run()
    return () => {
      mounted = false
    }
  }, [pacoteId])

  const payload = (): PacoteInput => ({
    nome: nome.trim(),
    descricao: descricao.trim(),
    preco: Number(preco),
    vip,
    warzoneCoin,
    pontos: Number(pontos),
    diasVip: vip ? Number(diasVip) : 0,
    descricaoVip: vip ? (descricaoVip.trim() ? descricaoVip.trim() : null) : null
  })

  const validate = (): string | null => {
    const p = payload()
    if (!p.nome) return 'Informe o nome.'
    if (!p.descricao) return 'Informe a descrição.'
    if (!Number.isFinite(p.preco) || p.preco <= 0) return 'Informe um preço válido.'
    if (!Number.isFinite(p.pontos) || p.pontos < 0) return 'Informe pontos válidos.'
    if (p.vip) {
      if (!Number.isFinite(p.diasVip) || p.diasVip <= 0) return 'Informe dias VIP válidos.'
    }
    return null
  }

  const onSave = async () => {
    const err = validate()
    if (err) {
      setError(err)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const updated = await apiService.atualizarPacote(pacoteId, payload())
      setData(updated)
    } catch (e: any) {
      setError(e?.message || 'Erro ao atualizar pacote.')
    } finally {
      setLoading(false)
    }
  }

  const onUpdateImage = async () => {
    if (!imagem) {
      setError('Selecione uma imagem.')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const updated = await apiService.atualizarPacoteImagem(pacoteId, imagem)
      setData(updated)
      setImagem(null)
    } catch (e: any) {
      setError(e?.message || 'Erro ao atualizar imagem.')
    } finally {
      setLoading(false)
    }
  }

  const onDelete = async () => {
    if (!data) return
    setDeleteError(null)
    setDeleteOpen(true)
  }

  const closeDelete = () => {
    if (deleteLoading) return
    setDeleteOpen(false)
  }

  const confirmDelete = async () => {
    if (!data) return
    setDeleteLoading(true)
    setDeleteError(null)
    try {
      await apiService.apagarPacote(pacoteId)
      navigate('/admin/pacotes', { replace: true })
    } catch (e: any) {
      setDeleteError(e?.message || 'Erro ao apagar pacote.')
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart />
              Editar Pacote
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {data ? (
                <>
                  <strong>{data.nome}</strong> • {brl(Number(data.preco) || 0)} • ID: <strong>{data.id}</strong>
                </>
              ) : (
                'Carregando...'
              )}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/pacotes')} disabled={loading}>
              Voltar
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={onSave} disabled={!canSave}>
              Salvar
            </Button>
            <Button variant="outlined" color="error" startIcon={<Delete />} onClick={onDelete} disabled={!canSave}>
              Apagar
            </Button>
          </Stack>
        </Box>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              {data && (
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Avatar
                    variant="rounded"
                    src={data.urlImagem ?? undefined}
                    alt={data.nome}
                    sx={{ width: 58, height: 58, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {String(data.nome ?? '?').slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {data.urlImagem ? 'Imagem cadastrada' : 'Sem imagem'}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <FormControlLabel
                control={<Checkbox checked={vip} onChange={(e) => setVip(e.target.checked)} disabled={loading} />}
                label="VIP"
              />
              <FormControlLabel
                control={<Checkbox checked={warzoneCoin} onChange={(e) => setWarzoneCoin(e.target.checked)} disabled={loading} />}
                label="Warzone Coin"
              />

              <Divider />

              <TextField
                label="Nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                fullWidth
                required
                disabled={loading}
              />

              <TextField
                label="Descrição"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                fullWidth
                required
                multiline
                minRows={3}
                disabled={loading}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Preço"
                  value={preco}
                  onChange={(e) => setPreco(e.target.value)}
                  type="number"
                  inputProps={{ step: '0.01', min: '0' }}
                  fullWidth
                  required
                  disabled={loading}
                />
                <TextField
                  label="Pontos"
                  value={pontos}
                  onChange={(e) => setPontos(e.target.value)}
                  type="number"
                  inputProps={{ step: '1', min: '0' }}
                  fullWidth
                  required
                  disabled={loading}
                />
              </Stack>

              {vip && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Dias VIP"
                    value={diasVip}
                    onChange={(e) => setDiasVip(e.target.value)}
                    type="number"
                    inputProps={{ step: '1', min: '1' }}
                    fullWidth
                    required
                    disabled={loading}
                  />
                  <TextField
                    label="Descrição VIP"
                    value={descricaoVip}
                    onChange={(e) => setDescricaoVip(e.target.value)}
                    fullWidth
                    disabled={loading}
                  />
                </Stack>
              )}

              <Divider />

              <Typography sx={{ fontWeight: 900 }}>Atualizar imagem</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ alignItems: { sm: 'center' } }}>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />} disabled={loading}>
                  Selecionar imagem
                  <input hidden type="file" accept="image/*" onChange={(e) => setImagem(e.target.files?.[0] ?? null)} />
                </Button>
                <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                  {imagem ? imagem.name : 'Nenhum arquivo selecionado.'}
                </Typography>
                <Button variant="contained" onClick={onUpdateImage} disabled={loading || !imagem}>
                  Enviar
                </Button>
              </Stack>
              <Alert severity="info">
                Envie uma imagem para atualizar a capa do pacote.
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        fullWidth
        maxWidth="xs"
        BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.78)' } }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(15, 15, 28, 0.96)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 24px 90px rgba(0,0,0,0.65)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Typography sx={{ color: 'text.secondary' }}>
              Tem certeza que deseja apagar o pacote <strong>{data?.nome ?? '—'}</strong>? Essa ação não pode ser desfeita.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDelete} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" startIcon={<Delete />} onClick={confirmDelete} disabled={deleteLoading || !data}>
            {deleteLoading ? 'Apagando...' : 'Apagar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AdminPacoteEditPage
